import { createContext, PropsWithChildren, useEffect, useMemo, useRef } from 'react'

import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useRouteMatch } from 'react-router'

import { IApplicationState } from '@store'
import {
  fetchCampaign,
  ICampaign,
  saveCampaign,
  selectCampaign,
  selectCampaignIsSaving,
  selectIsSocialCampaign,
  selectIsUnlockedCampaign,
} from '@store/campaigns'

export interface ICampaignContextProps {
  saveCampaign?: typeof saveCampaign
  campaign: ICampaign
  isCampaignFetched: boolean
  isSaving: boolean
  isOpen?: boolean
  isSocialCampaign?: boolean
  isUnlockedCampaign?: boolean
}

export const CampaignContext = createContext({ campaign: {} } as ICampaignContextProps)

// Base component for testing
export const CampaignProvider: React.FC<
  PropsWithChildren<
    ICampaignContextProps & {
      campaignId: number
      fetchCampaign: typeof fetchCampaign
      history: any
    }
  >
> = ({
  children,
  campaign,
  saveCampaign: saveCampaignAction,
  isCampaignFetched,
  isSaving,
  isSocialCampaign,
  isUnlockedCampaign,
  campaignId,
  fetchCampaign: fetchCampaignAction,
  history,
}) => {
  const calledRef = useRef(false)

  // Call fetchCampaign once on mount when campaignId is present.
  useEffect(() => {
    if (campaignId && !calledRef.current) {
      fetchCampaignAction(campaignId, { history })
      calledRef.current = true
    }
  }, [campaignId, fetchCampaignAction, history])

  const contextProps: ICampaignContextProps = useMemo(
    () => ({
      campaign,
      isCampaignFetched,
      saveCampaign: saveCampaignAction,
      isSaving,
      isSocialCampaign,
      isUnlockedCampaign,
    }),
    [
      campaign,
      isCampaignFetched,
      saveCampaignAction,
      isSaving,
      isSocialCampaign,
      isUnlockedCampaign,
    ],
  )

  return <CampaignContext.Provider value={contextProps}>{children}</CampaignContext.Provider>
}

// Connected component using hooks
const ConnectedCampaignProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const match = useRouteMatch()
  const history = useHistory()
  const dispatch = useDispatch()

  const campaignId = parseInt((match.params as any).campaignId, 10)
  const campaign = useSelector((state: IApplicationState) => selectCampaign(state, campaignId))
  const isCampaignFetched = Boolean(campaign)
  const isSaving = useSelector(selectCampaignIsSaving)
  const isSocialCampaign = useSelector((state: IApplicationState) =>
    selectIsSocialCampaign(state, campaignId),
  )
  const isUnlockedCampaign = useSelector((state: IApplicationState) =>
    selectIsUnlockedCampaign(state, campaignId),
  )

  return (
    <CampaignProvider
      campaign={campaign}
      isCampaignFetched={isCampaignFetched}
      isSaving={isSaving}
      isSocialCampaign={isSocialCampaign}
      isUnlockedCampaign={isUnlockedCampaign}
      campaignId={campaignId}
      fetchCampaign={(cId, opts) => dispatch(fetchCampaign(cId, opts))}
      saveCampaign={(...args) => dispatch(saveCampaign(...args))}
      history={history}
    >
      {children}
    </CampaignProvider>
  )
}

export default ConnectedCampaignProvider
